import React, { useState, useMemo, useCallback } from 'react';
import MaterialTable from 'material-table';
import { IconButton, Tooltip, withStyles, Typography } from '@material-ui/core';
import {
    BackButton,
    localization,
    hasAtLeastOnePermission,
    getDimensionsOfPagination,
    hasPermission
} from '../../sc-utilities';
import { Refresh, NoteAdd, SyncAlt, SupervisedUserCircle, CloudUpload, AllInbox, Clear } from '@material-ui/icons';
import * as actions from './store/pcProductActions';
import * as action from '../../np-core/Auth/store/authActions';
import { connect } from 'react-redux';
import Toolbar from '../../sc-utilities/Toolbar'
import { MuiTableRows } from '../../../themes';
import { fetchBulkChangeProductsGroup } from "../../sc-group/Group/store/groupActions";
import ChangeGroupModal from "../../sc-users/User/ChangeGroupModal";
import NICDelete from '../../sc-productComponents/NIC/NICDelete';
import * as diagnoseActions from '../../sc-diagnose/Diagnose/store/diagnoseActions';

const styles = theme => ({
    "@global": {
        "[class*='buttonsCustomer']": {
            margin: theme.spacing(),
            fontSize: '13px',
            width: '100%',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.buttonText.default
            },
        },
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.buttonText.default,
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});
let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
const tableRef = React.createRef();
const PCProductList = props => {
    const { classes, translations, saveColumnsFilter, tableColumns, onDownloadReport, fetchPCProduct, fetchPCProductCount, activeCustomerID, activeWarehouseID,
        warehouseActive, setWarehouseActive, permissions, onBulkChangeProductGroup, groups, onSendExternalData, onSendErasureReports, onClearDiagnoseResultsPerProducts } = props;

    const columnObject = useMemo(() => tableColumns, [tableColumns]);
    const [changeGroupModalState, setChangeGroupModalState] = useState({ open: false, ProductIDs: [] });
    const [clearDiagnoseResultsModalState, setClearDiagnoseResultsModalState] = useState({ open: false, ProductIDs: [] });

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: true,
        selection: hasAtLeastOnePermission(permissions, ["product_generate_report", "product_change_group"]),
        exportFileName: 'PCProduct',
        filterCellStyle: { maxWidth: '150px' },
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: [20, 50, 100],
        loading: true,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const downloadErasureReport = useCallback(async (ids) => {
        tableRef.current.onAllSelected(false);
        await onDownloadReport({ PCProductIDs: ids, ActiveCustomerID: activeCustomerID, ErasureReportType: "pdf" })
    }, [activeCustomerID, onDownloadReport]);

    const downloadDiagnoseReport = useCallback(async (ids) => {
        tableRef.current.onAllSelected(false);
        await onDownloadReport({ PCProductIDs: ids, ActiveCustomerID: activeCustomerID, ErasureReportType: "diagnose-pdf" })
    }, [activeCustomerID, onDownloadReport]);

    const sendExternalData = useCallback(async (ids) => {
        tableRef.current.onAllSelected(false);
        await onSendExternalData({ ProductIDs: ids, ActiveCustomerID: activeCustomerID })
    }, [activeCustomerID, onSendExternalData]);

    const sendErasureReports = useCallback(async (ids) => {
        tableRef.current.onAllSelected(false);
        await onSendErasureReports({ ProductIDs: ids, ActiveCustomerID: activeCustomerID })
    }, [activeCustomerID, onSendErasureReports]);

    const handleBulkChangeUsersGroup = ProductIDs => {
        setChangeGroupModalState({ open: true, ProductIDs });
    };

    const handleChangeGroupCloseSafe = () => {
        setChangeGroupModalState({ open: false });
    };

    const handleChangeGroupCloseDanger = async (GroupID) => {
        await onBulkChangeProductGroup({ ProductIDs: changeGroupModalState.ProductIDs, ActiveCustomerID: activeCustomerID, GroupID });
        setChangeGroupModalState({ open: false });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkClearDiagnoseResults = ProductIDs => {
        setClearDiagnoseResultsModalState({ open: true, ProductIDs });
    };

    const handleClearDiagnoseResultsCloseSafe = () => {
        setClearDiagnoseResultsModalState({ open: false });
    };

    const handleClearDiagnoseResultsCloseDanger = async () => {
        await onClearDiagnoseResultsPerProducts({ ProductIDs: clearDiagnoseResultsModalState.ProductIDs });
        setClearDiagnoseResultsModalState({ open: false });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const tableActions = [
        {
            tooltip: translations['download_erasure_report'] || "Download erasure report",
            icon: <NoteAdd />,
            onClick: (rowData) => downloadErasureReport(rowData.map(pcProduct => pcProduct.ProductID))
        },
        {
            tooltip: translations['download_diagnose_report'] || "Download diagnose report",
            icon: <NoteAdd />,
            onClick: (rowData) => downloadDiagnoseReport(rowData.map(pcProduct => pcProduct.ProductID))
        },
        {
            tooltip: translations['send_external_data'] || "Send external data",
            icon: <CloudUpload />,
            onClick: (rowData) => sendExternalData(rowData.map(pcProduct => pcProduct.ProductID))
        },
        {
            tooltip: translations['send_erasure_reports'] || "Send erasure reports",
            icon: <AllInbox />,
            onClick: (rowData) => sendErasureReports(rowData.map(pcProduct => pcProduct.ProductID))
        },
        hasPermission(permissions, 'product_change_group') ? {
            tooltip: translations["change_group"] || "Change group",
            icon: <SupervisedUserCircle />,
            onClick: (rowData) => handleBulkChangeUsersGroup(rowData.map(product => product.ProductID))
        } : {},
        hasPermission(permissions, 'system_diagnose_case_round_results_clear') ? {
            tooltip: translations["clear_diagnose_results"] || "Clear diagnose results",
            icon: <Clear />,
            onClick: (rowData) => handleBulkClearDiagnoseResults(rowData.map(product => product.ProductID))
        } : {},
    ];

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = { ...q, WarehouseID: warehouseActive.name[0] ? warehouseActive.name[0].value : warehouseActive.data[0] ? warehouseActive.data[0].value : activeWarehouseID };
        let count = await fetchPCProductCount(query);
        if (!query['page'] && !rowsPageSizeInit.includes(query['pageSize'])) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await fetchPCProduct(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })

        objToSendColumn[`PCProduct`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>  {translations["pc_product"] || 'PC product'} ({warehouseActive.name.length > 0 ? warehouseActive.name[0].displayValue : ''})</Typography>
                <Tooltip title={translations["change_warehouse_id"] || "Change active warehouse"}>
                    <IconButton aria-label={translations["change_warehouse_id"] || "Change active warehouse"} component="div" onClick={() => setWarehouseActive({ ...warehouseActive, open: true })}>
                        <SyncAlt id={'refresh_button'} />
                    </IconButton>
                </Tooltip>
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };
    const toolbar = {
        Toolbar: props => (
            <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
        )
    }

    return (<>
        <MaterialTable
            tableRef={tableRef}
            title={tableTitle()}
            columns={columnObject}
            data={fetchTableData}
            options={tableOptions}
            components={toolbar}
            onChangeRowsPerPage={(size) => {
                setTableOptions({ ...tableOptions, loading: false, pageSize: size })
                pageSize = size
            }}
            localization={localization(translations)}
            onChangeColumnHidden={columnHidden}
            onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
        />

        {hasPermission(permissions, 'product_change_group') ? (
            <ChangeGroupModal
                groups={groups}
                entityName={translations['users'] || 'Users'}
                translations={translations}
                handleCloseDanger={handleChangeGroupCloseDanger}
                handleCloseSafe={handleChangeGroupCloseSafe}
                modalState={changeGroupModalState}
                permissions={permissions}
            />
        ) : null}

        {hasPermission(permissions, "diagnose_case_round_results_clear") ? (
            <NICDelete
                activeCustomer={activeCustomerID}
                entityName={translations["diagnose_results"] || "Diagnose results"}
                translations={translations}
                handleCloseDanger={handleClearDiagnoseResultsCloseDanger}
                handleDelete={handleClearDiagnoseResultsCloseSafe}
                deleteModalState={clearDiagnoseResultsModalState}
            />
        ) : null}
    </>)
}

const mapDispatchToProps = dispatch => {
    return {
        fetchPCProductCount: filter => dispatch(actions.fetchPCProductCount(filter)),
        onDownloadReport: params => dispatch(actions.downloadReport(params)),
        onSendExternalData: params => dispatch(actions.sendExternalData(params)),
        onSendErasureReports: params => dispatch(actions.sendErasureReports(params)),
        fetchPCProduct: filter => dispatch(actions.fetchPCProduct(filter)),
        onBulkGenerateReports: (pcProducts, translations) => dispatch(actions.generateBulkReports(pcProducts, translations)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkChangeProductGroup: data => dispatch(fetchBulkChangeProductsGroup(data)),
        onClearDiagnoseResultsPerProducts: data => dispatch(diagnoseActions.clearDiagnoseResultsPerProducts(data)),
    };
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        activeWarehouseID: state.core.login.user.ActiveWarehouseID,
        pcProducts: state.product.pcProducts,
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(PCProductList));